// src/components/reports/SectionNotesModal.jsx
// vCIO commentary pinned to a report section, for one client + month.
//
// A section can read badly at face value — Hardware & lifecycle listing
// four-year-old servers — when there is context the client should hear. These
// notes put that context at the foot of the section it explains.
//
// CLIENT-FACING by design: what is written here goes on the client's report.
// That is stated on screen, because this modal sits next to Credential hygiene,
// which is the exact opposite and never leaves the app.
import { useState, useEffect, useCallback, useMemo } from "react";
import {
  getReportSections, getSectionNotes, addSectionNote, updateSectionNote, deleteSectionNote,
} from "../../api/monthlyReportsApi";

const btn = (primary) => ({
  padding: "6px 11px", borderRadius: 7, fontSize: 11.5, fontWeight: 600, cursor: "pointer",
  border: primary ? "none" : "1px solid var(--border,#d0d0d0)",
  background: primary ? "var(--dt-secondary,#0b80a5)" : "var(--card,#fff)",
  color: primary ? "#fff" : "var(--text,#222)",
});
const inp = {
  width: "100%", padding: "7px 9px", borderRadius: 7, fontSize: 12,
  border: "1px solid var(--border,#d0d0d0)", background: "var(--card,#fff)",
  color: "var(--text,#222)", fontFamily: "inherit",
};

export default function SectionNotesModal({ companyId, companyName, period, periodLabel, onClose, toast }) {
  const [sections, setSections] = useState([]);
  const [notes, setNotes] = useState(null);          // null = loading
  const [sectionKey, setSectionKey] = useState("");
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(null);      // { id, sectionKey, text }

  // The dropdown is fed by the BACKEND registry, never a list in this file —
  // the renderer and this picker must agree about what a section is, and a copy
  // here would drift the first time one is added or renamed.
  useEffect(() => {
    getReportSections()
      .then((s) => { setSections(s); setSectionKey((k) => k || s[0]?.key || ""); })
      .catch(() => toast("Could not load the report's section list", "err"));
  }, [toast]);

  const load = useCallback(() => {
    setNotes(null);
    getSectionNotes(companyId, period)
      .then(setNotes)
      .catch(() => { setNotes([]); toast("Could not load notes", "err"); });
  }, [companyId, period, toast]);
  useEffect(load, [load]);

  const titleOf = useCallback(
    (k) => sections.find((s) => s.key === k)?.title || k, [sections]);

  // Grouped for display, but kept in REGISTRY order rather than insertion order,
  // so the list reads down the report the way the client will meet it.
  const grouped = useMemo(() => {
    const by = {};
    (notes || []).forEach((n) => { (by[n.sectionKey] = by[n.sectionKey] || []).push(n); });
    return sections.filter((s) => by[s.key]?.length).map((s) => ({ ...s, items: by[s.key] }));
  }, [notes, sections]);

  const doAdd = async () => {
    const t = text.trim();
    if (!t) return toast("Nothing to add — write the note first.", "err");
    if (!sectionKey) return toast("Pick a section.", "err");
    setBusy(true);
    try {
      await addSectionNote(companyId, period, { sectionKey, text: t });
      setText("");
      load();
      toast(`Note added to ${titleOf(sectionKey)}.`);
    } catch (e) { toast(e?.response?.data?.error || e.message || "Could not add the note", "err"); }
    finally { setBusy(false); }
  };

  const doSaveEdit = async () => {
    const t = String(editing.text || "").trim();
    if (!t) return toast("A note cannot be empty — delete it instead.", "err");
    setBusy(true);
    try {
      await updateSectionNote(companyId, period, editing.id, { sectionKey: editing.sectionKey, text: t });
      setEditing(null);
      load();
      toast("Note updated.");
    } catch (e) { toast(e?.response?.data?.error || e.message || "Could not update the note", "err"); }
    finally { setBusy(false); }
  };

  const doDelete = async (n) => {
    if (!window.confirm(`Delete this note from ${titleOf(n.sectionKey)}?\n\nIt will stop appearing on the report the next time it is generated.`)) return;
    setBusy(true);
    try {
      await deleteSectionNote(companyId, period, n.id);
      load();
      toast("Note deleted.");
    } catch (e) { toast(e?.response?.data?.error || e.message || "Could not delete the note", "err"); }
    finally { setBusy(false); }
  };

  return (
    <div onClick={() => !busy && onClose()}
      style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.55)", zIndex: 620, display: "grid", placeItems: "center", padding: 14 }}>
      <div onClick={(e) => e.stopPropagation()}
        style={{ background: "var(--card,#fff)", borderRadius: 12, maxWidth: 720, width: "100%", maxHeight: "88vh", display: "flex", flexDirection: "column", overflow: "hidden" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "11px 15px", borderBottom: "1px solid var(--border,#eee)" }}>
          <div style={{ fontWeight: 700, fontSize: 13.5 }}>
            📝 Section notes — {companyName}
            <span style={{ color: "var(--text3,#999)", fontWeight: 500 }}> · {periodLabel || period}</span>
          </div>
          <button style={{ ...btn(false), marginLeft: "auto" }} onClick={onClose}>✕ Close</button>
        </div>

        <div style={{ padding: 15, overflowY: "auto" }}>
          <div style={{ fontSize: 11.5, color: "var(--text3,#777)", lineHeight: 1.6, marginBottom: 12 }}>
            A note appears at the <strong>bottom of the section you choose</strong>, in this month's
            report. Use it to give context a section cannot show on its own — why old hardware is
            still online, why a number looks worse than it is.
            {" "}<strong>These are written for the client and appear on their report.</strong>
          </div>

          {/* Add */}
          <div style={{ border: "1px solid var(--border,#eee)", borderRadius: 9, padding: 11, marginBottom: 14 }}>
            <label style={{ display: "block", fontSize: 9, fontWeight: 700, letterSpacing: ".06em", textTransform: "uppercase", color: "var(--text3,#888)", marginBottom: 3 }}>
              Section
            </label>
            <select style={{ ...inp, marginBottom: 8 }} value={sectionKey} disabled={busy}
                    onChange={(e) => setSectionKey(e.target.value)}>
              {sections.map((s) => <option key={s.key} value={s.key}>{s.title}</option>)}
            </select>
            <textarea style={{ ...inp, resize: "vertical" }} rows={3} disabled={busy}
                      placeholder="e.g. The two 2019 servers are kept online only as a safety net during the migration, and will be decommissioned once cutover completes."
                      value={text} onChange={(e) => setText(e.target.value)} />
            <div style={{ display: "flex", gap: 8, alignItems: "center", marginTop: 8 }}>
              <button style={btn(true)} disabled={busy || !text.trim()} onClick={doAdd}>Add note</button>
              <span style={{ fontSize: 10.5, color: "var(--text3,#999)" }}>
                Several notes can sit on one section, and on different sections.
              </span>
            </div>
          </div>

          {/* List */}
          {notes === null && <div style={{ fontSize: 12, color: "var(--text3,#999)" }}>Loading…</div>}
          {notes && !notes.length && (
            <div style={{ fontSize: 12, color: "var(--text3,#999)" }}>
              No notes for {periodLabel || period} yet — the report renders exactly as it does now.
            </div>
          )}

          {grouped.map((g) => (
            <div key={g.key} style={{ marginBottom: 13 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: "var(--dt-secondary,#0b80a5)", marginBottom: 5 }}>
                {g.title}
              </div>
              {g.items.map((n) => (
                <div key={n.id} style={{ border: "1px solid var(--border,#eee)", borderRadius: 8, padding: 9, marginBottom: 6 }}>
                  {editing?.id === n.id ? (
                    <>
                      <select style={{ ...inp, marginBottom: 6 }} value={editing.sectionKey} disabled={busy}
                              onChange={(e) => setEditing((x) => ({ ...x, sectionKey: e.target.value }))}>
                        {sections.map((s) => <option key={s.key} value={s.key}>{s.title}</option>)}
                      </select>
                      <textarea style={{ ...inp, resize: "vertical" }} rows={3} disabled={busy}
                                value={editing.text}
                                onChange={(e) => setEditing((x) => ({ ...x, text: e.target.value }))} />
                      <div style={{ display: "flex", gap: 6, marginTop: 6 }}>
                        <button style={btn(true)} disabled={busy} onClick={doSaveEdit}>Save</button>
                        <button style={btn(false)} disabled={busy} onClick={() => setEditing(null)}>Cancel</button>
                      </div>
                    </>
                  ) : (
                    <>
                      <div style={{ fontSize: 12, color: "var(--text,#222)", lineHeight: 1.55, whiteSpace: "pre-wrap" }}>{n.text}</div>
                      <div style={{ display: "flex", gap: 6, alignItems: "center", marginTop: 7 }}>
                        <span style={{ fontSize: 10, color: "var(--text3,#aaa)" }}>
                          {n.updatedAt && n.updatedAt !== n.createdAt ? "edited " : "added "}
                          {new Date(n.updatedAt || n.createdAt).toLocaleDateString("en-AU", { day: "2-digit", month: "short" })}
                        </span>
                        <button style={{ ...btn(false), marginLeft: "auto" }} disabled={busy}
                                onClick={() => setEditing({ id: n.id, sectionKey: n.sectionKey, text: n.text })}>Edit</button>
                        <button style={{ ...btn(false), color: "#c0392b" }} disabled={busy}
                                onClick={() => doDelete(n)}>Delete</button>
                      </div>
                    </>
                  )}
                </div>
              ))}
            </div>
          ))}

          {notes?.length > 0 && (
            <div style={{ fontSize: 10.5, color: "var(--text3,#999)", borderTop: "1px solid var(--border,#eee)", paddingTop: 9 }}>
              Notes are read when the report is generated — regenerate to see changes.
              A note whose section has no data this month will not appear, because there is no
              section for it to sit under.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
